import React from 'react'
import PropTypes from 'prop-types'
import { Link, graphql } from 'gatsby'
import Img from 'gatsby-image'

import tw, { css, styled } from 'twin.macro'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import localeCheck from '../components/utils/locale-check'
/** Components */
import Layout from '../components/layout'
import Meta from '../components/meta'
import { Underline } from 'react-feather'
import filterlocale from '../components/utils/filter-locale'
/** Styles with Emotion & Tailwindcss */
const Container = tw.div`mx-auto py-8 w-11/12`
const BreadCrumbs = tw.ol`flex text-sm`
const Crumb = tw.li`flex`
const DateTime = tw.time`text-xs text-gray-700`
const Content = styled.main`
	${tw`flex flex-col text-base my-8`}
`
const BlockQuote = styled.blockquote`
	padding: 1em;
	border-left: 5px solid #999;
	&:before {
		display: none;
	}
	&:not(:first-of-type) {
		margin-top: 0.5em;
	}
	p {
		color: #555;
		font-size: 12pt;
		line-height: 1.4;
		${tw`font-display`} /* font-family: 'PT Serif', Cambria, 'Hoefler Text', Utopia, 'Liberation Serif',
			'Nimbus Roman No9 L Regular', Times, 'Times New Roman', serif; */
	}
	code {
		margin-top: 0.5em;
		padding: 0;
		color: #777;
		font-size: 12pt;
		text-align: left;
		font-style: italic;
		&:before {
			content: '— ';
		}
	}
	&:nth-of-type(even) {
		text-align: right;
		border-left: none;
		border-right: 5px solid #999;
	}
	&:nth-of-type(even) code {
		text-align: right;
	}
	&:nth-of-type(even) code:before {
		content: '';
	}
	&:nth-of-type(even) code:after {
		content: ' —';
	}
	@media screen and (min-width: 300px) {
		padding: 1em 20% 1em 1em;
		p {
			font-size: 14pt;
		}
		&:nth-of-type(even) {
			padding: 1em 1em 1em 20%;
		}
	}
`

// new styles

const ImageAsset = tw.div`my-2`
const Bold = tw.b`font-bold`
const Italic = tw.i`italic`
const UnderlinedText = tw.u`underline`
const CodeText = tw.code``
const Text = tw.p`font-display text-base mb-2 leading-snug tracking-wide px-4`
const UnorderedList = styled.ul`
	${tw`mx-8 px-4 mb-2`}

	> li {
		${tw`list-disc`}
	}
	> li > p {
		${tw`text-sm pl-0`}
	}
`
const OrderedList = styled.ol`
	${tw`mx-8 px-4 mb-2`}

	> li {
		${tw`list-decimal`}
	}
	> li > p {
		${tw`text-sm pl-0`}
	}
`
const Heading1 = styled.h1`
	${tw`font-display font-bold text-4xl mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase`}
`
const Heading2 = styled.h2`
	${tw`font-display font-bold text-3xl mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase mt-4`}
`
const Heading3 = styled.h3`
	${tw`font-display font-bold text-2xl mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase mt-4`}
`
const Heading4 = styled.h4`
	${tw`font-display font-bold text-xl mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase mt-4`}
`
const Heading5 = styled.h5`
	${tw`font-display font-bold text-lg mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase mt-4`}
`
const Heading6 = styled.h6`
	${tw`font-display font-bold text-base mb-6 text-blue-500 bg-gray-200 py-2 pl-6 pr-2 border-l-4 border-blue-500 uppercase mt-4`}
`
const HyperLinkText = tw.a`font-display underline italic text-blue-500 hover:no-underline cursor-pointer`

/**
 * @constant PageTemplate
 * @description 'Create Pages from ContentfulPages array'
 */
const PageTemplate = ({ data, pageContext }) => {
	const { prodslug, locale, defaultLocale } = pageContext

	const [{ node }] = filterlocale(data.page.edges, locale)
	const { slug, seoKeywords, seoTitle, seoDescription, body, updatedAt } = node
	/** Render "Rich Text" from Contentful to the DOM */
	const options = {
		renderMark: {
			[MARKS.BOLD]: text => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: text => <Italic>{text}</Italic>,
			[MARKS.UNDERLINE]: text => <UnderlinedText>{text}</UnderlinedText>,
			[MARKS.CODE]: text => <CodeText>{text}</CodeText>,
		},
		renderNode: {
			[BLOCKS.HEADING_1]: (node, children) => <Heading1>{children}</Heading1>,
			[BLOCKS.HEADING_2]: (node, children) => <Heading2>{children}</Heading2>,
			[BLOCKS.HEADING_3]: (node, children) => <Heading3>{children}</Heading3>,
			[BLOCKS.HEADING_4]: (node, children) => <Heading4>{children}</Heading4>,
			[BLOCKS.HEADING_5]: (node, children) => <Heading5>{children}</Heading5>,
			[BLOCKS.HEADING_6]: (node, children) => <Heading6>{children}</Heading6>,
			[BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
			[BLOCKS.UL_LIST]: (node, children) => (
				<UnorderedList>{children}</UnorderedList>
			),
			[BLOCKS.OL_LIST]: (node, children) => (
				<OrderedList>{children}</OrderedList>
			),
			[BLOCKS.EMBEDDED_ASSET]: node => {
				return (
					<ImageAsset>
						<img
							src={node?.data?.target?.fields?.file['en-AU']?.url}
							alt={node?.data?.target?.fields?.description['en-AU']}
						/>
					</ImageAsset>
				)
			},
			[BLOCKS.QUOTE]: (node, children) => <BlockQuote>{children}</BlockQuote>,
			[INLINES.HYPERLINK]: (node, children) => {
				const text = node.content[0].value
				return (
					<HyperLinkText href={node.data.uri} target="_blank">
						{text}
					</HyperLinkText>
				)
			},
		},
	}

	const checkedLocale = localeCheck(locale, defaultLocale)

	return (
		<Layout>
			<Meta
				title={seoTitle}
				keywords={seoKeywords}
				description={seoDescription}
				lang={locale}
			/>
			<Container>
				<BreadCrumbs vocab="https://schema.org/" typeof="BreadcrumbList">
					<Crumb property="itemListElement" typeof="ListItem">
						<Link to={`${checkedLocale}`} property="item" typeof="WebPage">
							<span property="name">Home</span>
						</Link>
						<meta property="position" content="1" />
					</Crumb>
					&nbsp;{'/'}&nbsp;
					<Crumb property="itemListElement" typeof="ListItem">
						<Link
							to={`${checkedLocale}/${slug}/`}
							property="item"
							typeof="WebPage"
						>
							<b><h1 property="name">{seoTitle}</h1></b>
						</Link>
						<meta property="position" content="2" />
					</Crumb>
				</BreadCrumbs>
				<Content>
					{body ? documentToReactComponents(body.json, options) : ''}
					<DateTime datetime={updatedAt}>
						<b>Last updated on</b>: {updatedAt}
					</DateTime>
				</Content>
			</Container>
		</Layout>
	)
}
export default PageTemplate

PageTemplate.propTypes = {
	data: PropTypes.object,
}

export const PageQuery = graphql`
	query PageBySlug($slug: String!) {
		page: allContentfulPage(filter: { slug: { eq: $slug } }) {
			edges {
				node {
					slug
					seoKeywords
					seoTitle
					seoDescription
					node_locale
					body {
						json
					}
					updatedAt(locale: "en-AU", formatString: "llll")
				}
			}
		}
	}
`
